import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Wallet, Building, Building2, Pencil, Trash2, Plus,
  Search, Download, RefreshCw, DollarSign, BarChart3, PieChart
} from 'lucide-react';
import { bankService } from '../api/bankService';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import useGranularPermissions from '../hooks/useGranularPermissions';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, Legend
} from 'recharts';

const CHART_COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899'];

function Banque() {
  const {
    canCreate,
    canUpdate,
    canDelete,
    hasPermission
  } = useGranularPermissions();
  const canView = hasPermission('banque', 'view');

  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });

  const [activeTab, setActiveTab] = useState('entrees');
  const [loading, setLoading] = useState(true);

  // Data state
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);

  // Filters
  const [selectedAccountFilter, setSelectedAccountFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  // Modals state
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Forms state
  const [accountForm, setAccountForm] = useState({
    id: null, name: '', bank_name: '', agency: '', account_number: '',
    initial_balance: 0, logo_url: null, status: 'active'
  });
  const [transactionForm, setTransactionForm] = useState({
    id: null,
    bank_account_id: '',
    type: 'inflow',
    transaction_type: 'deposit',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    reference: '',
    category: ''
  });

  // ─── Formatting helpers ─────────────────────────────────────────────
  const formatCurrency = (value) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

  const formatDate = (d) => {
    try {
      return format(new Date(d), 'dd MMM yyyy', { locale: fr });
    } catch {
      return '–';
    }
  };

  // ─── Data fetching ──────────────────────────────────────────────────
  const fetchAccounts = useCallback(async () => {
    try {
      const data = await bankService.getAccounts(currentUser, { _t: Date.now() });
      setAccounts(data || []);
    } catch (error) {
      console.error('Erreur comptes:', error);
      toast.error('Erreur lors du chargement des comptes bancaires');
    }
  }, [currentUser]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedAccountFilter !== 'all') {
        params.bank_account_id = selectedAccountFilter;
      }
      if (dateRange.from) params.start_date = format(dateRange.from, "yyyy-MM-dd'T'00:00:00.000'Z'");
      if (dateRange.to) params.end_date = format(dateRange.to, "yyyy-MM-dd'T'23:59:59.999'Z'");

      const data = await bankService.getTransactions(currentUser, params);
      setTransactions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedAccountFilter, dateRange]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (selectedAccountFilter !== 'all') {
        params.bank_account_id = selectedAccountFilter;
      }
      if (dateRange.from) params.start_date = format(dateRange.from, "yyyy-MM-dd'T'00:00:00.000'Z'");
      if (dateRange.to) params.end_date = format(dateRange.to, "yyyy-MM-dd'T'23:59:59.999'Z'");

      const data = await bankService.getStats(currentUser, params);
      setStats(data);
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  }, [currentUser, selectedAccountFilter, dateRange]);

  useEffect(() => {
    if (currentUser) fetchAccounts();
  }, [currentUser, fetchAccounts]);

  useEffect(() => {
    if (currentUser && accounts.length > 0) {
      fetchTransactions();
      fetchStats();
    }
  }, [currentUser, accounts, fetchTransactions, fetchStats]);

  // ─── Derived data ───────────────────────────────────────────────────
  const inflows = transactions.filter(tx => tx.type === 'inflow');
  const outflows = transactions.filter(tx => tx.type === 'outflow');

  const filteredInflows = inflows.filter(tx =>
    (tx.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (tx.reference || '').toLowerCase().includes(search.toLowerCase())
  );
  const filteredOutflows = outflows.filter(tx =>
    (tx.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (tx.reference || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalEntrees = stats?.inflows?.total || 0;
  const totalSorties = stats?.outflows?.total || 0;
  const soldeActuel = stats?.solde_actuel ?? (totalEntrees - totalSorties);
  const nbEntrees = stats?.inflows?.count || 0;
  const nbSorties = stats?.outflows?.count || 0;

  // ─── Tabs config (matching Caisse.js pattern) ───────────────────────
  const tabs = [
    { key: 'entrees', label: 'ENTRÉES', sub: 'Encaissements bancaires', icon: <TrendingUp className="w-5 h-5" />, color: 'green' },
    { key: 'sorties', label: 'SORTIES', sub: 'Décaissements bancaires', icon: <TrendingDown className="w-5 h-5" />, color: 'red' },
    { key: 'solde', label: 'SOLDE', sub: 'Vue globale', icon: <Building2 className="w-5 h-5" />, color: 'blue' },
  ];

  // ─── KPIs ───────────────────────────────────────────────────────────
  const entreeKpis = [
    { label: 'Total Entrées', value: formatCurrency(totalEntrees), sub: 'Ce mois', icon: <TrendingUp className="w-5 h-5 text-green-600" />, color: 'green' },
    { label: "Nombre d'entrées", value: nbEntrees, sub: 'Opérations', icon: <BarChart3 className="w-5 h-5 text-blue-600" />, color: 'blue' },
    { label: 'Moyenne', value: formatCurrency(stats?.inflows?.avg || 0), sub: 'Par opération', icon: <DollarSign className="w-5 h-5 text-purple-600" />, color: 'purple' },
    { label: 'Plus grande entrée', value: formatCurrency(stats?.inflows?.largest?.amount || 0), sub: stats?.inflows?.largest?.date ? formatDate(stats.inflows.largest.date) : '–', icon: <Wallet className="w-5 h-5 text-emerald-600" />, color: 'emerald' },
  ];

  const sortieKpis = [
    { label: 'Total Sorties', value: formatCurrency(totalSorties), sub: 'Ce mois', icon: <TrendingDown className="w-5 h-5 text-red-600" />, color: 'red' },
    { label: "Nombre de sorties", value: nbSorties, sub: 'Opérations', icon: <BarChart3 className="w-5 h-5 text-orange-600" />, color: 'orange' },
    { label: 'Moyenne', value: formatCurrency(stats?.outflows?.avg || 0), sub: 'Par opération', icon: <DollarSign className="w-5 h-5 text-purple-600" />, color: 'purple' },
    { label: 'Plus grande sortie', value: formatCurrency(stats?.outflows?.largest?.amount || 0), sub: stats?.outflows?.largest?.date ? formatDate(stats.outflows.largest.date) : '–', icon: <Wallet className="w-5 h-5 text-red-600" />, color: 'red' },
  ];

  const soldeKpis = [
    { label: 'Solde Actuel', value: formatCurrency(soldeActuel), sub: 'Tous les comptes', icon: <Wallet className="w-5 h-5 text-blue-600" />, color: 'blue' },
    { label: 'Total Entrées', value: formatCurrency(totalEntrees), sub: 'Ce mois', icon: <TrendingUp className="w-5 h-5 text-green-600" />, color: 'green' },
    { label: 'Total Sorties', value: formatCurrency(totalSorties), sub: 'Ce mois', icon: <TrendingDown className="w-5 h-5 text-red-600" />, color: 'red' },
  ];

  const kpiColorMap = {
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-l-green-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-l-blue-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-l-red-500' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-l-orange-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-l-purple-500' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-l-emerald-500' },
  };

  // ─── Handlers ───────────────────────────────────────────────────────
  const handleResetFilters = () => {
    setSearch('');
    setSelectedAccountFilter('all');
    setDateRange({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) });
  };

  const handleExport = (exportFormat) => {
    let data = [];
    let columns = [];
    let title = '';
    let filename = '';

    if (activeTab === 'entrees') {
      data = filteredInflows;
      columns = [
        { header: 'Date', dataKey: 'date' },
        { header: 'Opération', dataKey: 'transaction_type_label' },
        { header: 'Compte', dataKey: 'account_name' },
        { header: 'Référence', dataKey: 'reference' },
        { header: 'Description', dataKey: 'description' },
        { header: 'Montant (DZD)', dataKey: 'amount' },
      ];
      title = 'Rapport des Entrées Bancaires';
      filename = 'Entrees_Banque';
    } else if (activeTab === 'sorties') {
      data = filteredOutflows;
      columns = [
        { header: 'Date', dataKey: 'date' },
        { header: 'Opération', dataKey: 'transaction_type_label' },
        { header: 'Compte', dataKey: 'account_name' },
        { header: 'Référence', dataKey: 'reference' },
        { header: 'Description', dataKey: 'description' },
        { header: 'Montant (DZD)', dataKey: 'amount' },
      ];
      title = 'Rapport des Sorties Bancaires';
      filename = 'Sorties_Banque';
    } else {
      return;
    }

    const formattedData = data.map(item => ({
      ...item,
      date: item.date ? format(parseSafeDate(item.date), 'dd/MM/yyyy') : '',
      transaction_type_label: item.transaction_type === 'deposit' ? 'Versement / Encaissement' :
                              item.transaction_type === 'withdrawal' ? 'Retrait Espèces' :
                              item.transaction_type === 'transfer' ? 'Virement' :
                              item.transaction_type === 'payment' ? 'Paiement par chèque/carte' :
                              item.transaction_type === 'bank_fee' ? 'Frais bancaires' : item.transaction_type,
      account_name: accounts.find(a => a.id === item.bank_account_id)?.name || 'Inconnu'
    }));

    if (exportFormat === 'csv') exportToCSV(formattedData, filename, columns);
    else if (exportFormat === 'excel') exportToExcel(formattedData, filename, columns);
    else if (exportFormat === 'pdf') exportToPDF(formattedData, filename, columns, title);
  };

  const handleOpenAccountModal = (account = null) => {
    if (account) {
      setAccountForm({ ...account, initial_balance: account.initial_balance ?? 0 });
    } else {
      setAccountForm({ id: null, name: '', bank_name: '', agency: '', account_number: '', initial_balance: 0, logo_url: null, status: 'active' });
    }
    setShowAccountModal(true);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Logo trop volumineux (max 500 Ko)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setAccountForm(prev => ({ ...prev, logo_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = {
        name: accountForm.name,
        bank_name: accountForm.bank_name,
        agency: accountForm.agency,
        account_number: accountForm.account_number,
        initial_balance: parseFloat(accountForm.initial_balance) || 0,
        logo_url: accountForm.logo_url || null,
        status: accountForm.status
      };
      if (accountForm.id) {
        await bankService.updateAccount(currentUser, accountForm.id, dataToSave);
        toast.success('Compte mis à jour avec succès');
      } else {
        await bankService.createAccount(currentUser, dataToSave);
        toast.success('Compte ajouté avec succès');
      }
      setShowAccountModal(false);
      fetchAccounts();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement du compte");
    }
  };

  const handleOpenTransactionModal = (transaction = null, defaultType = 'inflow') => {
    if (transaction) {
      setTransactionForm({
        ...transaction,
        bank_account_id: transaction.bank_account_id ?? (accounts.length > 0 ? accounts[0].id : ''),
        type: transaction.type ?? defaultType,
        transaction_type: transaction.transaction_type ?? (defaultType === 'inflow' ? 'deposit' : 'transfer'),
        date: transaction.date ? parseSafeDate(transaction.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        amount: transaction.amount ?? '',
        description: transaction.description ?? '',
        reference: transaction.reference ?? '',
        category: transaction.category ?? ''
      });
    } else {
      setTransactionForm({
        id: null,
        bank_account_id: selectedAccountFilter !== 'all' ? selectedAccountFilter : (accounts.length > 0 ? accounts[0].id : ''),
        type: defaultType,
        transaction_type: defaultType === 'inflow' ? 'deposit' : 'transfer',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        description: '',
        reference: '',
        category: ''
      });
    }
    setShowTransactionModal(true);
  };

  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    try {
      const rawDate = transactionForm.date || new Date().toISOString().split('T')[0];
      const dateIso = rawDate.includes('T') ? rawDate : `${rawDate}T12:00:00.000Z`;

      const dataToSave = {
        bank_account_id: transactionForm.bank_account_id,
        type: transactionForm.type,
        transaction_type: transactionForm.transaction_type,
        date: dateIso,
        amount: parseFloat(transactionForm.amount),
        description: transactionForm.description,
        reference: transactionForm.reference || null,
        category: transactionForm.category || null
      };
      if (transactionForm.id) {
        await bankService.updateTransaction(currentUser, transactionForm.id, dataToSave);
        toast.success('Transaction mise à jour avec succès');
      } else {
        await bankService.createTransaction(currentUser, dataToSave);
        toast.success('Transaction ajoutée avec succès');
      }
      setShowTransactionModal(false);
      fetchTransactions();
      fetchStats();
      fetchAccounts();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement de la transaction");
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      await bankService.deleteTransaction(currentUser, deleteConfirm.id);
      toast.success('Transaction supprimée avec succès');
      setDeleteConfirm(null);
      fetchTransactions();
      fetchStats();
      fetchAccounts();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await bankService.deleteAccount(currentUser, deleteConfirm.id);
      toast.success('Compte supprimé avec succès');
      setDeleteConfirm(null);
      fetchAccounts();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression du compte');
    }
  };

  const executeDelete = () => {
    if (deleteConfirm?.type === 'account') handleDeleteAccount();
    else if (deleteConfirm?.type === 'transaction') handleDeleteTransaction();
  };

  const getAccountName = (accId) => accounts.find(a => a.id === accId)?.name || 'Compte inconnu';

  // ─── Transaction table rendering (shared for entrées & sorties) ─────
  const renderTransactionTable = (txList, type) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">Compte</TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">Opération</TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">Référence</TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Montant</TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {txList.map(tx => (
              <TableRow key={tx.id} className="hover:bg-gray-50 border-b border-gray-100">
                <TableCell className="whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(tx.date)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200" variant="outline">
                    {getAccountName(tx.bank_account_id)}
                  </Badge>
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-gray-600 capitalize">{(tx.transaction_type || '').replace('_', ' ')}</TableCell>
                <TableCell className="whitespace-nowrap text-sm text-gray-700">{tx.description || '–'}</TableCell>
                <TableCell className="whitespace-nowrap text-sm text-gray-500">{tx.reference || '–'}</TableCell>
                <TableCell className={`text-right text-sm font-black ${type === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                  {type === 'inflow' ? '+' : '-'}{formatCurrency(tx.amount)} DA
                </TableCell>
                <TableCell className="whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-1">
                    {canUpdate('banque') && (
                    <button
                      className="p-1.5 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors"
                      onClick={() => handleOpenTransactionModal(tx)}
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    )}
                    {canDelete('banque') && (
                    <button
                      className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                      onClick={() => setDeleteConfirm({ type: 'transaction', id: tx.id })}
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {txList.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-gray-400">
                  Aucune {type === 'inflow' ? 'entrée' : 'sortie'} trouvée
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // ─── Solde Tab Chart Data ───────────────────────────────────────────
  const evolutionData = (stats?.evolution || []).map(d => ({
    date: d.date,
    balance: d.balance,
    label: (() => { try { return format(parseSafeDate(d.date), 'dd MMM', { locale: fr }); } catch { return d.date; } })()
  }));

  const distributionData = (stats?.distribution || []).filter(d => d.balance !== 0).map(d => ({
    name: d.name,
    value: Math.abs(d.balance)
  }));

  // ─── RENDER ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 fade-in bg-gray-50 min-h-screen p-3 sm:p-6">
      {/* En-tête */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Banque</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Gérez vos comptes bancaires et mouvements</p>
        </div>

      </div>

      {/* Filtres + Action */}
      <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">

            <select
              className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              value={selectedAccountFilter}
              onChange={(e) => setSelectedAccountFilter(e.target.value)}
            >
              <option value="all">Tous les comptes</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name} - {acc.bank_name}</option>
              ))}
            </select>

            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-2.5 hover:bg-gray-50 transition-colors"
              title="Réinitialiser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-sm bg-gray-100 text-gray-700 border border-gray-200 rounded-lg px-4 py-2.5 hover:bg-gray-200 transition-colors">
                  <Download className="w-4 h-4" /> Exporter
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
                  Exporter en PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                  Exporter en Excel (XLSX)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                  Exporter en CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <ExpenseDateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} compact={true} />
            {activeTab === 'entrees' && canCreate('banque') && (
              <button
                onClick={() => handleOpenTransactionModal(null, 'inflow')}
                className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 shadow-sm"
              >
                <Plus className="w-5 h-5" /> Nouvelle Entrée
              </button>
            )}
            {activeTab === 'sorties' && canCreate('banque') && (
              <button
                onClick={() => handleOpenTransactionModal(null, 'outflow')}
                className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 shadow-sm"
              >
                <Plus className="w-5 h-5" /> Nouvelle Sortie
              </button>
            )}
            {activeTab === 'solde' && canCreate('banque') && (
              <button
                onClick={() => handleOpenAccountModal()}
                className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-teal-600 border-teal-200 hover:bg-teal-50 hover:border-teal-300 shadow-sm"
              >
                <Plus className="w-5 h-5" /> Ajouter un compte
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Toggle Entrées / Sorties / Comptes */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setActiveTab('entrees')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
            activeTab === 'entrees'
              ? 'bg-green-600 text-white border-green-600 shadow-lg'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-green-50 hover:border-green-200'
          }`}
        >
          <TrendingUp className="w-5 h-5" />
          Entrées
        </button>
        <button
          onClick={() => setActiveTab('sorties')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
            activeTab === 'sorties'
              ? 'bg-red-600 text-white border-red-600 shadow-lg'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-red-50 hover:border-red-200'
          }`}
        >
          <TrendingDown className="w-5 h-5" />
          Sorties
        </button>
        <button
          onClick={() => setActiveTab('solde')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
            activeTab === 'solde'
              ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-blue-50 hover:border-blue-200'
          }`}
        >
          <Building2 className="w-5 h-5" />
          Comptes / Solde
        </button>
      </div>

      {/* CONTENT */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-6">
        {/* Search */}
        {(activeTab === 'entrees' || activeTab === 'sorties') && (
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">
              {activeTab === 'entrees' ? 'Entrées Bancaires' : 'Sorties Bancaires'}
            </h2>
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Rechercher..."
                className="w-full sm:w-64 pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
          {(activeTab === 'entrees' ? entreeKpis : activeTab === 'sorties' ? sortieKpis : soldeKpis).map((kpi, i) => {
            const colors = kpiColorMap[kpi.color] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-l-gray-500' };
            return (
              <Card key={i} className={`stat-card border-l-4 ${colors.border} hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center`}>
                      {kpi.icon}
                    </div>
                    <CardTitle className="text-sm font-medium text-gray-600">{kpi.label}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${colors.text} mb-1`}>{kpi.value}</div>
                  {kpi.sub && kpi.sub !== '–' && (
                    <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{kpi.sub}</span>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* ENTRÉES TAB */}
        {activeTab === 'entrees' && renderTransactionTable(filteredInflows, 'inflow')}

        {/* SORTIES TAB */}
        {activeTab === 'sorties' && renderTransactionTable(filteredOutflows, 'outflow')}

        {/* SOLDE TAB */}
        {activeTab === 'solde' && (
          <>
            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Balance Evolution Line Chart */}
              <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-500" /> Évolution du Solde
                </h3>
                <div style={{ width: '100%', height: 280 }}>
                  {evolutionData.length > 0 ? (
                    <ResponsiveContainer>
                      <LineChart data={evolutionData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <Tooltip
                          formatter={(value) => [`${formatCurrency(value)} DA`, 'Solde']}
                          labelFormatter={(label) => label}
                        />
                        <Line type="monotone" dataKey="balance" stroke="#0ea5e9" strokeWidth={2.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              </div>

              {/* Distribution Donut Chart */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-purple-500" /> Répartition par Compte
                </h3>
                <div style={{ width: '100%', height: 280 }}>
                  {distributionData.length > 0 ? (
                    <ResponsiveContainer>
                      <RechartsPieChart>
                        <Pie
                          data={distributionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {distributionData.map((_, idx) => (
                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => `${formatCurrency(v)} DA`} />
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 11 }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                      Aucune donnée
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Accounts Cards Grid */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4">Comptes Bancaires</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {accounts.map((acc, idx) => {
                  const CARD_GRADIENTS = [
                    'from-red-500 to-red-700',
                    'from-teal-500 to-teal-700',
                    'from-green-500 to-green-700',
                    'from-purple-500 to-purple-700',
                    'from-blue-500 to-blue-700',
                    'from-orange-500 to-orange-600',
                    'from-indigo-500 to-indigo-700',
                    'from-rose-500 to-rose-700',
                  ];
                  const gradient = acc.status === 'active'
                    ? CARD_GRADIENTS[idx % CARD_GRADIENTS.length]
                    : 'from-gray-500 to-gray-600';
                  return (
                    <div key={acc.id} className={`rounded-xl shadow-md hover:shadow-lg transition-all overflow-hidden border border-gray-100 ${
                      acc.status !== 'active' ? 'opacity-65 grayscale-[30%] bg-gray-50' : 'bg-white'
                    }`}>
                      {/* Colored header */}
                      <div className={`bg-gradient-to-r ${gradient} px-5 py-4 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                          {acc.logo_url ? (
                            <img src={acc.logo_url} alt={acc.bank_name} className="w-10 h-10 rounded-full object-cover bg-white p-0.5 shadow" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                              <Building size={20} className="text-white" />
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-white text-sm leading-tight truncate max-w-[140px]">{acc.bank_name}</div>
                            <div className="text-white/70 text-xs truncate max-w-[140px]">{acc.agency}</div>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          acc.status === 'active'
                            ? 'bg-white/25 text-white'
                            : 'bg-gray-700/40 text-gray-200'
                        }`}>
                          {acc.status === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      {/* Body */}
                      <div className="bg-white px-5 py-4 space-y-3">
                        <div>
                          <div className="text-xs text-gray-400 uppercase tracking-wide font-medium">N° COMPTE / RIB</div>
                          <div className="text-sm font-semibold text-gray-800 mt-0.5">{acc.account_number || '–'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase tracking-wide font-medium">SOLDE ACTUEL</div>
                          <div className="text-xl font-black text-gray-900 mt-0.5">{formatCurrency(acc.current_balance)} DA</div>
                        </div>
                        {acc.name && acc.name !== acc.bank_name && (
                          <div className="text-xs text-gray-400 italic">{acc.name}</div>
                        )}
                      </div>
                      {/* Footer actions */}
                      <div className="bg-gray-50 border-t border-gray-100 px-5 py-3 flex items-center justify-between">
                        {canUpdate('banque') && (
                        <button
                          className="text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                          onClick={() => {
                            bankService.updateAccount(currentUser, acc.id, { ...acc, status: acc.status === 'active' ? 'inactive' : 'active' })
                              .then(() => { toast.success('Statut mis à jour'); fetchAccounts(); })
                              .catch(() => toast.error('Erreur'));
                          }}
                        >
                          {acc.status === 'active' ? 'Désactiver' : 'Activer'}
                        </button>
                        )}
                        <div className="flex gap-3">
                          {canUpdate('banque') && (
                          <button
                            className="text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors"
                            onClick={() => handleOpenAccountModal(acc)}
                          >
                            Modifier
                          </button>
                          )}
                          {canDelete('banque') && (
                          <button
                            className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors"
                            onClick={() => setDeleteConfirm({ type: 'account', id: acc.id })}
                          >
                            Supprimer
                          </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {accounts.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    Aucun compte bancaire enregistré. Cliquez sur "Ajouter un compte" pour commencer.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── MODALS ──────────────────────────────────────────────────── */}

      {/* Account Form Modal */}
      <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
        <DialogContent className="sm:max-w-[480px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{accountForm.id ? 'Modifier le compte' : 'Ajouter un compte bancaire'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveAccount} className="space-y-4 pt-4">

            {/* Logo upload */}
            <div className="space-y-2">
              <Label>Logo de la banque (optionnel)</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                  {accountForm.logo_url ? (
                    <img src={accountForm.logo_url} alt="logo" className="w-full h-full object-cover" />
                  ) : (
                    <Building size={24} className="text-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    id="logo-upload"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                  <label
                    htmlFor="logo-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4" /> Choisir un logo
                  </label>
                  {accountForm.logo_url && (
                    <button
                      type="button"
                      className="ml-2 text-xs text-red-500 hover:text-red-700"
                      onClick={() => setAccountForm(prev => ({ ...prev, logo_url: null }))}
                    >
                      Supprimer
                    </button>
                  )}
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG — max 500 Ko</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bank_account_name">Nom du compte</Label>
                <input
                  id="bank_account_name"
                  name="bank_account_name"
                  required type="text"
                  placeholder="Compte Principal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={accountForm.name ?? ''}
                  onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank_name">Nom de la banque</Label>
                <input
                  id="bank_name"
                  name="bank_name"
                  required type="text"
                  placeholder="BDL, CPA, BNA..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={accountForm.bank_name ?? ''}
                  onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bank_agency">Agence</Label>
                <input
                  id="bank_agency"
                  name="bank_agency"
                  required type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={accountForm.agency ?? ''}
                  onChange={(e) => setAccountForm({ ...accountForm, agency: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank_account_status">Statut</Label>
                <select
                  id="bank_account_status"
                  name="bank_account_status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={accountForm.status ?? 'active'}
                  onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })}
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_number">Numéro de compte (RIB)</Label>
              <input
                id="account_number"
                name="account_number"
                required type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="001 00456 0300 123456 78"
                value={accountForm.account_number ?? ''}
                onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="initial_balance">Solde initial (DA)</Label>
              <input
                id="initial_balance"
                name="initial_balance"
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="0.00"
                value={accountForm.initial_balance ?? ''}
                onChange={(e) => setAccountForm({ ...accountForm, initial_balance: e.target.value })}
              />
              <p className="text-xs text-gray-400">Solde de départ au moment de la création du compte</p>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAccountModal(false)}>Annuler</Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction Form Modal */}
      <Dialog open={showTransactionModal} onOpenChange={setShowTransactionModal}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" aria-describedby={undefined}>
          <DialogHeader className={`px-6 py-6 m-0 ${transactionForm.type === 'inflow' ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gradient-to-r from-orange-500 to-red-600'}`}>
            <DialogTitle className="text-2xl font-bold text-white">
              {transactionForm.id 
                ? 'Modifier la transaction' 
                : transactionForm.type === 'inflow' ? 'Nouvelle Entrée' : 'Nouvelle Sortie'}
            </DialogTitle>
            <DialogDescription className="text-white/80 mt-1 text-sm">
              Enregistrez les détails de la transaction bancaire
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTransaction} className="p-6 space-y-6 bg-gray-50/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700" htmlFor="tx_date">Date</Label>
                <input
                  id="tx_date"
                  name="tx_date"
                  required type="date"
                  className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={transactionForm.date ?? ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700" htmlFor="tx_bank_account_id">Compte Bancaire</Label>
                <select
                  id="tx_bank_account_id"
                  name="tx_bank_account_id"
                  required
                  className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={transactionForm.bank_account_id ?? ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, bank_account_id: e.target.value })}
                >
                  <option value="" disabled>Sélectionner un compte</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name} - {acc.bank_name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700" htmlFor="tx_transaction_type">Type d'opération</Label>
                <select
                  id="tx_transaction_type"
                  name="tx_transaction_type"
                  className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={transactionForm.transaction_type ?? ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, transaction_type: e.target.value })}
                >
                  {transactionForm.type === 'inflow' ? (
                    <option value="deposit">Versement / Encaissement</option>
                  ) : (
                    <>
                      <option value="transfer">Virement</option>
                      <option value="payment">Paiement par chèque/carte</option>
                      <option value="bank_fee">Frais bancaires</option>
                    </>
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700" htmlFor="tx_amount">Montant (DA)</Label>
                <input
                  id="tx_amount"
                  name="tx_amount"
                  required type="number" step="0.01" min="0"
                  className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={transactionForm.amount ?? ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-semibold text-gray-700" htmlFor="tx_description">Description</Label>
                <input
                  id="tx_description"
                  name="tx_description"
                  required type="text"
                  className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Motif de la transaction"
                  value={transactionForm.description ?? ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-semibold text-gray-700" htmlFor="tx_reference">Référence (Optionnel)</Label>
                <input
                  id="tx_reference"
                  name="tx_reference"
                  type="text"
                  className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="N° Chèque, Référence virement..."
                  value={transactionForm.reference ?? ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter className="pt-6 mt-6 border-t border-gray-100 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowTransactionModal(false)} className="h-11 rounded-xl px-6">Annuler</Button>
              <Button type="submit" className={`h-11 rounded-xl px-6 text-white font-medium ${transactionForm.type === 'inflow' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-orange-600 hover:bg-orange-700'}`}>Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-[420px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {deleteConfirm?.type === 'account' ? (
              <>
                <p className="text-sm text-gray-700">
                  Êtes-vous sûr de vouloir supprimer ce compte bancaire ?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-700">⚠️ Attention — suppression en cascade</p>
                  <p className="text-sm text-red-600 mt-1">
                    Toutes les transactions associées à ce compte seront également supprimées définitivement.
                  </p>
                </div>
                <p className="text-xs text-gray-400">Cette action est irréversible.</p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                Êtes-vous sûr de vouloir supprimer cette transaction ? Cette action est irréversible.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
            <Button type="button" className="bg-red-600 hover:bg-red-700 text-white" onClick={executeDelete}>
              {deleteConfirm?.type === 'account' ? 'Supprimer le compte et les transactions' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Banque;
